// 일일 던전(황금 보물고·강화석 광산)과 무한의 탑 한 판의 흐름(L9a, PRD 5절).
// 입장(enterDaily) → 판 계획 로드(FlowController.loadMap custom 경로) → 진행(보물고 60초 제한·문구) → 클리어/실패 정산.
// 보상의 정본은 일일 장부(shared/daily.ts)이고, 이 파일은 연산(dungeonEnter/Done·towerStart/Clear/End)을 순서대로 적은 뒤
// settleDaily(dailyPay.ts)로 지갑에 넣는다. 연산 id가 판마다 새로 정해지고 장부가 같은 id를 거르므로 같은 클리어를 두 번 불러도 한 번만 지급된다.
// 스테이지 진행·첫 클리어·카드 보상·결과 영수증(카드)은 만들지 않는다. 처치 EXP는 판 EXP 기록(growth.commitRun)으로 남는다.
// 고정 60Hz step 안에서는 새 객체를 만들지 않는다(문구는 1초에 한 번·웨이브가 바뀔 때만 만든다).
import { Ev } from '../shared/events';
import { RUN_STALE_MS, TOWER_MAX, runEnteredAt, type DailyOp, type DungeonKind } from '../shared/daily';
import { VAULT_SECONDS, dailyLabel, dailyPlan, type DailyKind } from '../shared/content/dailyPlans';
import { towerThemeOf } from '../shared/content/towerHigh';
import { gridOfMap } from '../shared/stageMaps';
import { HeroAct, Scene } from '../shared/types';
import { settleDaily } from './dailyPay';
import type { GameSim } from './game';

/** Ev.DailyEnter b: 종류 번호 */
export const DAILY_KIND_NO: Readonly<Record<DailyKind, number>> = { vault: 0, mine: 1, tower: 2 };
/** Ev.DailyEnter c: 장부 거절 이유 번호(daily.ts DailyCode 선언 순서, 0 = 거절 아님) */
export const DAILY_CODE_NO: Readonly<Record<string, number>> = {
  ok: 0, read_only: 1, duplicate: 2, locked: 3, not_ready: 4, already_claimed: 5, no_attempts: 6, in_run: 7, not_in_run: 8, unknown: 9, bad_floor: 10, tower_done: 11, tower_idle: 12, save_failed: 13,
};
/** Ev.DailyEnter c: 장부 밖 거절 이유 — 입장할 수 없는 장면 / 미선택 보상 결과가 남아 있음 / 판 불러오기 실패 */
export const DAILY_DENY_SCENE = 20;
export const DAILY_DENY_BUSY = 21;
export const DAILY_DENY_LOAD = 22;

const TICK_HZ = 60;
const VAULT_TICKS = VAULT_SECONDS * TICK_HZ;

export class DailyRun {
  /** 일일 판이 진행 중인가(입장 성공 ~ 클리어/실패/떠남) */
  running = false;
  kind: DailyKind = 'vault';
  floor = 0;
  /** 마지막으로 문구를 갱신한 보물고 남은 초·웨이브 키(문구를 만드는 횟수를 줄인다) */
  private lastSec = -1;
  private lastKey = -1;
  private title = '';
  /** 저장 실패로 아직 적지 못한 클리어 연산(retryPending) */
  private pendingOp: DailyOp | null = null;
  private pendingTown = 1;

  constructor(private readonly g: GameSim) {}

  /** 일일 결과·보상이 아직 저장·지급되지 않았다(결과 화면 저장 대기 표시, PR #273 Codex 4차 P2). 저장소는 읽지 않는다 */
  get payoutPending(): boolean {
    const d = this.g.daily;
    if (this.pendingOp !== null || d.unsaved) return true;
    // 틱마다 불리므로 배열을 만들지 않고 센다
    const owed = d.peek.owed;
    for (let i = 0; i < owed.length; i++) if (!owed[i].paid) return true;
    return false;
  }

  private emitEnter(a: number, kind: DailyKind, code: number, floor: number): void {
    const e = this.g.emit(Ev.DailyEnter);
    e.a = a; e.b = DAILY_KIND_NO[kind]; e.c = code; e.d = floor;
  }

  /** 입장 명령. 마을(이동 중 아님) 또는 일일 판의 결과 화면(탑 이어 올라가기)에서만 */
  enter(kind: DailyKind): void {
    const g = this.g, f = g.flow, d = g.daily;
    const sceneOk = (f.scene === Scene.Village && !f.paused && f.travelTo === 0)
      || (f.scene === Scene.Result && !this.running && g.flowController.plan.daily !== undefined && g.result.dailyKind !== '' && !f.paused);
    if (!sceneOk) { this.emitEnter(0, kind, DAILY_DENY_SCENE, 0); return; }
    // 카드를 아직 안 고른 결과가 저장돼 있으면 loadMap이 그 결과를 되살리므로 시도 횟수를 쓰기 전에 거절한다
    if (g.save.unpickedResult()) { this.emitEnter(0, kind, DAILY_DENY_BUSY, 0); return; }
    // 앞 판의 결과(클리어·실패)를 아직 장부에 적지 못했다: 적힌 뒤에 들어간다(하루 횟수·탑 1회 규칙 유지)
    if (this.pendingOp) { this.retryPending(); if (this.pendingOp) { this.emitEnter(0, kind, DAILY_CODE_NO.save_failed, 0); return; } }
    const town = g.highestTown(), region6 = g.gear.protectUnlocked;
    let floor = 0;
    if (kind === 'tower') {
      let t = d.refresh().tower;
      const day = d.view().day;
      if (!(t.active && t.startedDay === day)) {
        const res = d.apply({ id: d.nextOpId('tower-start'), t: 'towerStart' }, town, region6);
        if (res.code !== 'ok') { this.emitEnter(0, kind, DAILY_CODE_NO[res.code] ?? 9, 0); return; }
        t = res.state.tower;
      }
      floor = t.floor + 1;
      if (floor > TOWER_MAX) { this.emitEnter(0, kind, DAILY_CODE_NO.tower_done, 0); return; }
    } else {
      let res = d.apply({ id: d.nextOpId('den-' + kind), t: 'dungeonEnter', kind }, town, region6);
      if (res.code === 'in_run' && this.staleRun()) {
        // 이전 판이 결과 없이 끊겼다(충돌·강제 종료): 그 시도는 이미 썼으니 실패로 닫고 한 번 더 시도한다.
        // 들어간 지 얼마 안 된 판은 다른 탭이 하고 있을 수 있어 닫지 않는다(in_run으로 거절, PR #273 Codex 2차 P2)
        const stuck = d.view().inRun;
        if (stuck) d.apply({ id: d.nextOpId('den-stuck-' + stuck), t: 'dungeonDone', kind: stuck, success: false }, town, region6);
        res = d.apply({ id: d.nextOpId('den-' + kind), t: 'dungeonEnter', kind }, town, region6);
      }
      if (res.code !== 'ok') { this.emitEnter(0, kind, DAILY_CODE_NO[res.code] ?? 9, 0); return; }
    }
    const plan = dailyPlan(kind, town, floor);
    this.title = dailyLabel(kind, floor);
    g.flowController.loadMap(plan.region, null, { scene: Scene.Dungeon, plan, map: plan.map, grid: gridOfMap(plan.map), label: this.title, objective: this.title });
    if (f.scene !== Scene.Dungeon || g.flowController.plan !== plan) {
      // 불러오기가 거절됐다: 쓴 시도를 되돌릴 수는 없으니 실패로 닫는다(탑은 도전을 그대로 둔다)
      if (kind !== 'tower') d.apply({ id: d.nextOpId('den-load-' + kind), t: 'dungeonDone', kind, success: false }, town, region6);
      this.emitEnter(0, kind, DAILY_DENY_LOAD, floor);
      return;
    }
    this.running = true; this.kind = kind; this.floor = floor; this.lastSec = -1; this.lastKey = -1;
    f.stageKind = 'daily'; f.stageId = kind === 'tower' ? 'tower:' + floor : 'daily:' + kind;
    // 앞 방은 정리된 상태로 두고 마지막 아레나 입구에 선다
    g.flowController.qaGotoRoom(plan.rooms.length - 1);
    f.stageLabel = this.title; f.objectiveLabel = this.title;
    this.emitEnter(1, kind, 0, floor);
  }

  /** 고정 스텝 1회(FlowController.step 끝). 보물고 제한 시간·진행 문구 */
  step(): void {
    if (!this.running) return;
    const g = this.g, f = g.flow;
    if (f.scene !== Scene.Dungeon) return;
    if (f.stageLabel !== this.title) f.stageLabel = this.title;
    if (this.kind === 'vault') {
      const left = VAULT_TICKS - f.elapsedTicks;
      // 마지막 몹을 잡은 뒤(클리어 연출 대기)에는 시간이 다 돼도 실패로 치지 않는다
      if (left <= 0 && !g.flowController.finalKill()) { this.timeout(); return; }
      const sec = left > 0 ? Math.ceil(left / TICK_HZ) : 0;
      if (sec !== this.lastSec) { this.lastSec = sec; f.objectiveLabel = '보물 고블린을 잡아라! 남은 시간 ' + sec + '초'; }
      return;
    }
    const key = f.roomIndex < 0 ? -1 : f.wave * 8 + f.roomState[f.roomIndex];
    if (key !== this.lastKey) {
      this.lastKey = key;
      f.objectiveLabel = this.kind === 'mine' ? '수정 골렘을 부숴라 (' + (f.wave + 1) + '/' + Math.max(1, f.waveCount) + ')'
        : g.flowController.plan.rooms[g.flowController.plan.rooms.length - 1].kind === 'boss' ? this.title + ' · ' + (towerThemeOf(this.floor)?.bossLine ?? '수호자를 쓰러뜨려라')
        : this.title + ' · 웨이브 ' + (f.wave + 1) + '/' + Math.max(1, f.waveCount);
    }
  }

  /** 클리어 정산(FlowController.stageClear가 일일 판이면 부른다). 같은 판을 두 번 불러도 한 번만 지급된다 */
  dailyClear(): void {
    if (!this.running) return;
    const g = this.g, f = g.flow, r = g.result, h = g.hero, d = g.daily;
    if (f.scene !== Scene.Dungeon) return;
    this.running = false;
    const town = g.highestTown(), region6 = g.gear.protectUnlocked, kind = this.kind;
    const id = d.nextOpId(kind === 'tower' ? 'tower-' + this.floor : 'den-done-' + kind);
    const res = kind === 'tower' ? d.apply({ id, t: 'towerClear', floor: this.floor }, town, region6)
      : d.apply({ id, t: 'dungeonDone', kind: kind as DungeonKind, success: true }, town, region6);
    const rw = res.code === 'ok' ? res.reward : undefined;
    // 저장소가 잠시 막혀 못 적었다: 같은 연산을 붙잡아 두고 다시 적는다(보상을 잃지 않게)
    if (res.code === 'save_failed' || res.code === 'read_only') {
      this.pendingOp = kind === 'tower' ? { id, t: 'towerClear', floor: this.floor } : { id, t: 'dungeonDone', kind: kind as DungeonKind, success: true };
      this.pendingTown = town;
    }
    if (f.roomIndex >= 0) f.roomState[f.roomIndex] = 2;
    f.objectiveKilled = f.objectiveTotal;
    g.emit(Ev.RoomClear).a = f.roomIndex;
    g.emit(Ev.StageClear);
    r.cleared = true; r.clearTicks = f.elapsedTicks; r.maxCombo = g.combo.best;
    r.starMask = 1; r.stars = 1; r.cards.fill(0); r.pickedCard = 0;
    r.resultId = 'daily:' + id; r.stageId = '';
    // 도감 2.0(C6): 일일 던전·탑 클리어도 이 판의 처치·보스를 영수증 id(연산 id)로 한 번 반영한다
    g.codex.settle(r.resultId, g.flowController.plan.regionNo, g.codex.runBossKey);
    r.dailyKind = kind; r.dailyFloor = this.floor;
    r.gold = rw ? rw.gold : 0; r.dailyStones = rw ? rw.stones : 0; r.dailyProtect = rw ? rw.protect : 0; r.dailyGems = rw ? rw.gems : 0;
    // 장부 연산이 거절되면(읽기 전용·충돌) 보상 없이 결과만 보인다. 지급은 지금 한 번 시도하고, 못 쓴 건은 tickSave가 이어서 시도한다
    settleDaily(g, true);
    const t = d.view().tower;
    r.dailyBest = t.best;
    r.dailyNext = kind === 'tower' && res.code === 'ok' && t.active && this.floor < TOWER_MAX ? this.floor + 1 : 0;
    g.growth.commitRun(r.resultId);
    g.loot.sweep();
    g.gear.flush(true);
    r.savePending = g.save.savePending || d.unsaved;
    r.saveRecovered = g.save.recovered > 0;
    g.profile.sync();
    f.scene = Scene.Result; f.sceneTick = 0; f.paused = false;
    f.objectiveLabel = res.code !== 'ok' ? (this.pendingOp ? '일일 기록 저장을 다시 시도하는 중이에요. 잠시만 기다려 주세요.' : '일일 기록을 저장하지 못했어요. 마을로 돌아가세요.')
      : kind === 'tower' ? (r.dailyNext > 0 ? this.floor + '층 클리어! 다음 ' + r.dailyNext + '층에 도전하거나 마을로 돌아가세요.' : '무한의 탑을 모두 올랐어요! 마을로 돌아가세요.')
      : (kind === 'vault' ? '보물고를 털었어요! ' : '광산을 캤어요! ') + '마을로 돌아가세요.';
    h.act = HeroAct.Victory; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.hitstop = 0;
    g.awakening.cancel();
    g.mobController.reset();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    g.emit(Ev.SceneChange).a = Scene.Result;
  }

  /** 실패 장부 기록: 보물고·광산 = 시도 소진(보상 없음), 탑 = 오늘 도전 끝(내일 최고 층 다음부터) */
  private recordFail(): void {
    const g = this.g, d = g.daily, town = g.highestTown(), region6 = g.gear.protectUnlocked, kind = this.kind, r = g.result;
    r.dailyKind = kind; r.dailyFloor = this.floor; r.dailyNext = 0; r.gold = 0;
    const op: DailyOp = kind === 'tower' ? { id: d.nextOpId('tower-end'), t: 'towerEnd' }
      : { id: d.nextOpId('den-done-' + kind), t: 'dungeonDone', kind: kind as DungeonKind, success: false };
    const res = d.apply(op, town, region6);
    // 실패 기록도 저장이 막히면 붙잡아 두고 다시 적는다: 탑 패배가 안 적히면 같은 날 다시 오를 수 있게 된다(PR #273 Codex 3차 P2)
    if (res.code === 'save_failed' || res.code === 'read_only') { this.pendingOp = op; this.pendingTown = town; }
    r.dailyBest = d.view().tower.best;
  }

  /** 패배(FlowController.defeat가 Defeat 장면으로 바꾼 직후) */
  dailyFail(): void {
    if (!this.running) return;
    this.running = false;
    this.recordFail();
    this.g.flow.objectiveLabel = this.kind === 'tower' ? '탑에서 쓰러졌어요. 오늘 도전은 끝났어요.' : '쓰러졌어요. 오늘 이 던전의 기회를 하나 썼어요.';
  }

  /** 보물고 제한 시간 초과: 부활 깃털 없이 바로 실패 장면 */
  private timeout(): void {
    const g = this.g, f = g.flow, h = g.hero;
    if (!this.running || f.scene !== Scene.Dungeon) return;
    this.running = false;
    this.recordFail();
    f.scene = Scene.Defeat; f.sceneTick = 0; f.paused = false;
    f.objectiveLabel = '시간 초과! 보물고가 닫혔어요. 오늘 이 던전의 기회를 하나 썼어요.';
    h.act = HeroAct.Idle; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.result.cleared = false; g.result.clearTicks = f.elapsedTicks; g.result.maxCombo = g.combo.best;
    g.awakening.cancel();
    g.mobController.reset();
    g.releaseAllTelegraphs();
    g.skillFx.clear();
    g.arrows.clear();
    g.growth.commitRun();
    g.gear.flush(true);
    g.emit(Ev.Defeat);
    g.emit(Ev.SceneChange).a = Scene.Defeat;
  }

  /** 판 도중 마을·다른 판으로 떠남(loadMap): 보물고·광산은 시도 소진 실패, 탑은 도전을 그대로 둔다(같은 층을 다시 오를 수 있다) */
  abandon(): void {
    if (!this.running) return;
    this.running = false;
    if (this.kind !== 'tower') this.recordFail();
  }

  /** 진행 중 던전이 끊긴 판인가: 들어간 지 RUN_STALE_MS가 지났거나 입장 영수증이 없다(다른 탭이 지금 하는 판을 닫지 않게) */
  private staleRun(): boolean {
    const at = runEnteredAt(this.g.daily.view());
    return at === null || this.g.wallClock() - at >= RUN_STALE_MS;
  }

  /**
   * 클리어 기록 재시도(PR #273 Codex 2차 P2): 클리어 순간 일일 장부 저장이 실패하면(연산은 되돌려진다) 같은 연산 id로 다시 적는다.
   * 성공하면 결과 화면 보상을 채우고 바로 지급한다. FlowController 정산 주기(1초)에서 부른다
   */
  retryPending(): void {
    const op = this.pendingOp;
    if (!op) return;
    const g = this.g, d = g.daily;
    const res = d.apply(op, this.pendingTown, g.gear.protectUnlocked);
    if (res.code === 'save_failed' || res.code === 'read_only') return;
    this.pendingOp = null;
    if (res.code !== 'ok') return;
    const r = g.result, rw = res.reward;
    if (r.resultId === 'daily:' + op.id && rw) {
      r.gold = rw.gold; r.dailyStones = rw.stones; r.dailyProtect = rw.protect; r.dailyGems = rw.gems;
      r.dailyBest = res.state.tower.best;
      r.dailyNext = op.t === 'towerClear' && res.state.tower.active && op.floor < TOWER_MAX ? op.floor + 1 : 0;
      g.flow.objectiveLabel = '일일 기록을 저장했어요. 보상이 들어갔어요.';
    }
    settleDaily(g, true);
    r.savePending = g.save.savePending || d.unsaved;
    g.profile.sync();
  }

  /** 판이 끊긴 채(새로고침·충돌) 남은 던전 도전을 실패로 닫는다. 부팅 때 한 번 */
  recover(): void {
    const g = this.g, d = g.daily, stuck = d.refresh().inRun;
    if (!stuck || this.running || !this.staleRun()) return;
    d.apply({ id: d.nextOpId('den-recover-' + stuck), t: 'dungeonDone', kind: stuck, success: false }, g.highestTown(), g.gear.protectUnlocked);
  }
}
